/**
 * Device-stored recent orders and reservations (localStorage).
 * Persists across tab close and return days later.
 */

const RECENT_ORDERS_KEY = 'qr_menu_recent_orders'
const RECENT_RESERVATIONS_KEY = 'qr_menu_recent_reservations'
const QR_ORDERS_KEY = 'qr_menu_qr_orders'
const MAX_ORDERS = 20
const MAX_RESERVATIONS = 20
const MAX_QR_ORDERS = 20
const MAX_AGE_DAYS = 30

export interface RecentOrderSnapshot {
  order_number: string
  customer_mobile: string
  total_amount: number
  created_at: string
  status: string
}

export interface RecentReservationSnapshot {
  id: string
  reservation_time: string
  party_size: number
  customer_name: string
  status: string
}

/** Waiter-scan payload encoded in the QR. Stored so we can show the same QR again in My Orders. */
export interface StoredQRPayload {
  v: number
  cn?: string
  cm?: string
  n?: string
  i: Array<{ mi: string; na: string; q: number; vn?: string; vpm?: number; ad?: string[]; ap?: number; bp: number; no?: string }>
}

/** Order created via QR (no order_number until waiter scans). Full details for display in My Orders. */
export interface QROrderSnapshot {
  id: string
  created_at: string
  customer_name?: string
  customer_mobile?: string
  order_notes?: string
  items: Array<{
    name: string
    quantity: number
    unit_price: number
    total_price: number
    variant_name?: string
    addons?: string[]
    notes?: string
  }>
  subtotal: number
  total: number
  status: 'pending_scan'
  /** Exact payload used for the QR so we can show it again in My Orders. */
  qrPayload?: StoredQRPayload
}

function getRecentOrdersRaw(): RecentOrderSnapshot[] {
  try {
    const raw = localStorage.getItem(RECENT_ORDERS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function getRecentReservationsRaw(): RecentReservationSnapshot[] {
  try {
    const raw = localStorage.getItem(RECENT_RESERVATIONS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function isOlderThanDays(isoDate: string, days: number): boolean {
  const then = new Date(isoDate).getTime()
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  return then < cutoff
}

/**
 * Add an order snapshot. Dedupes by order_number (prepend), caps at MAX_ORDERS, drops older than MAX_AGE_DAYS.
 */
export function addRecentOrder(snapshot: RecentOrderSnapshot): void {
  try {
    let list = getRecentOrdersRaw()
    list = list.filter((o) => o.order_number !== snapshot.order_number && !isOlderThanDays(o.created_at, MAX_AGE_DAYS))
    list.unshift(snapshot)
    list = list.slice(0, MAX_ORDERS)
    localStorage.setItem(RECENT_ORDERS_KEY, JSON.stringify(list))
  } catch (e) {
    console.error('Error saving recent order:', e)
  }
}

/**
 * Add a reservation snapshot. Dedupes by id (prepend), caps at MAX_RESERVATIONS, drops older than MAX_AGE_DAYS.
 */
export function addRecentReservation(snapshot: RecentReservationSnapshot): void {
  try {
    let list = getRecentReservationsRaw()
    list = list.filter((r) => r.id !== snapshot.id && !isOlderThanDays(r.reservation_time, MAX_AGE_DAYS))
    list.unshift(snapshot)
    list = list.slice(0, MAX_RESERVATIONS)
    localStorage.setItem(RECENT_RESERVATIONS_KEY, JSON.stringify(list))
  } catch (e) {
    console.error('Error saving recent reservation:', e)
  }
}

/**
 * Get recent orders from this device, newest first. Drops entries older than MAX_AGE_DAYS.
 */
export function getRecentOrders(): RecentOrderSnapshot[] {
  const list = getRecentOrdersRaw()
  return list.filter((o) => !isOlderThanDays(o.created_at, MAX_AGE_DAYS))
}

/**
 * Get recent reservations from this device, newest first. Drops entries older than MAX_AGE_DAYS.
 */
export function getRecentReservations(): RecentReservationSnapshot[] {
  const list = getRecentReservationsRaw()
  return list.filter((r) => !isOlderThanDays(r.reservation_time, MAX_AGE_DAYS))
}

function getQROrdersRaw(): QROrderSnapshot[] {
  try {
    const raw = localStorage.getItem(QR_ORDERS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const DEDUPE_WINDOW_MS = 15_000 // 15s: treat same-total orders as one (avoids double-add from Strict Mode / re-runs)

/**
 * Save a QR order (full details) when the customer generates the QR code. No order_number yet.
 * Dedupes by total + created_at within a short window so the same placement isn't stored multiple times.
 */
export function addQROrder(snapshot: QROrderSnapshot): void {
  try {
    const now = new Date(snapshot.created_at).getTime()
    let list = getQROrdersRaw()
    list = list.filter((r) => {
      if (r.id === snapshot.id) return false
      if (isOlderThanDays(r.created_at, MAX_AGE_DAYS)) return false
      // Drop recent QR order with same total (same placement added twice)
      const rTime = new Date(r.created_at).getTime()
      if (Math.abs(rTime - now) < DEDUPE_WINDOW_MS && r.total === snapshot.total) return false
      return true
    })
    list.unshift(snapshot)
    list = list.slice(0, MAX_QR_ORDERS)
    localStorage.setItem(QR_ORDERS_KEY, JSON.stringify(list))
  } catch (e) {
    console.error('Error saving QR order:', e)
  }
}

/**
 * Get QR orders (pending scan) from this device, newest first. Drops older than MAX_AGE_DAYS.
 * Collapses duplicates: same total and created within 15s are treated as one (keeps newest).
 */
export function getQROrders(): QROrderSnapshot[] {
  const list = getQROrdersRaw().filter((o) => !isOlderThanDays(o.created_at, MAX_AGE_DAYS))
  const deduped: QROrderSnapshot[] = []
  const windowMs = DEDUPE_WINDOW_MS
  for (const q of list) {
    const qTime = new Date(q.created_at).getTime()
    const isDup = deduped.some(
      (k) => k.total === q.total && Math.abs(new Date(k.created_at).getTime() - qTime) < windowMs
    )
    if (!isDup) deduped.push(q)
  }
  return deduped
}
